export enum CampaignStatus {
  SCHEDULED = 1,
  ACTIVE = 2,
  COMPLETED = 3,
}

export enum CampaignAction {
  NONE = 0,
  QUESTION = 1,
  SHOPIFY_URL = 2,
  SHOPIFY_DISCOUNT = 3,
  SHOPIFY_DELAYED = 4,
}

export enum CampaignQuestion {
  CUSTOM = 0,
  ADDRESS = 1,
  EMAIL = 2,
  PHONE_NUMBER = 3,
  NAME = 4,
  TWITTER = 5,
  POLL = 6,
  ALLOWLIST = 7,
  PUBLIC_POLL = 8,
}

export const statusToString = (status: CampaignStatus) => {
  switch (status) {
    case CampaignStatus.SCHEDULED:
      return 'Scheduled';
    case CampaignStatus.ACTIVE:
      return 'Active';
    case CampaignStatus.COMPLETED:
      return 'Completed';
    default:
      return 'Unknown';
  }
};

export enum CampaignType {
  ERC721 = 1,
  ERC1155 = 2,
}

export const campaignTypeToNumber = (type: CampaignType) => {
  switch (type) {
    case CampaignType.ERC1155:
      return 2;
    case CampaignType.ERC721:
      return 1;
    default:
      return 0;
  }
};

export const campaignTypeToString = (type: CampaignType) => {
  switch (type) {
    case CampaignType.ERC1155:
      return 'ERC1155';
    case CampaignType.ERC721:
      return 'ERC721';
    default:
      return 'Unknown';
  }
};

export const campaignActionToString = (type: CampaignAction) => {
  switch (type) {
    case CampaignAction.QUESTION:
      return 'Questionnaire';
    case CampaignAction.SHOPIFY_URL:
      return 'Shopify URL';
    case CampaignAction.SHOPIFY_DISCOUNT:
      return 'Shopify Discount';
    case CampaignAction.SHOPIFY_DELAYED:
      return 'Shopify Delayed';
    default:
      return 'Unknown';
  }
};

/** Snapshot bookkeeping from redeem-server's internal campaign response. */
export interface SnapshotData {
  /** The moment holders are counted at. */
  scheduledAt: number;
  /** Set once the snapshot has actually been recorded. */
  completedAt: number | null;
  /** Per-network block heights the snapshot was taken at. */
  cutoffBlockNumbers?: Record<string, number>;
}

export interface CampaignFull {
  id: number;
  cognitoUserId: string;
  name: string;
  status: CampaignStatus;
  type: CampaignType;
  snapshot: boolean;
  startDate: number | null;
  endDate: number | null;
  snapshotDate?: number;
  snapshotData?: SnapshotData | null;
  webhookUrl?: string;
  active?: boolean;
  // tokenAddress?: string,
  action: CampaignAction;
  redemptionCount?: number;
  maxRedemptions?: number;
  redemptionsPerToken?: number | null;
  redemptionsPerAddress?: number | null;

  statusString?: ReturnType<typeof statusToString>;
  actionString?: ReturnType<typeof campaignActionToString>;

  tokenData: {
    tokens: TokenWithNoFilter[];
    tokenRanges: TokenFilteredByIdRange[];
    tokenIds: TokenFilteredById[];
    tokenAttributes: TokenFilteredByAttributes[];
  };

  products: Omit<ShopifyProduct, 'productData'>[];
  rules: CampaignShopifyProductRules;
}

export interface Campaign {
  id: number;
  cognitoUserId: string;
  name: string;
  status: CampaignStatus;
  type: CampaignType;
  snapshot: boolean;
  startDate: number | null;
  endDate: number | null;
  snapshotDate?: number;
  snapshotData?: SnapshotData | null;
  webhookUrl?: string;
  active?: boolean;
  // tokenAddress?: string,
  action: CampaignAction;
  redemptionCount?: number;
  maxRedemptions?: number;
  redemptionsPerToken?: number | null;
  redemptionsPerAddress?: number | null;

  statusString?: ReturnType<typeof statusToString>;
  actionString?: ReturnType<typeof campaignActionToString>;

  tokenData: {
    tokens: TokenWithNoFilter[];
    tokenRanges: TokenFilteredByIdRange[];
    tokenIds: TokenFilteredById[];
    tokenAttributes: TokenFilteredByAttributes[];
  };

  products: Omit<ShopifyProduct, 'productData'>[];
  rules: CampaignShopifyProductRules;
}

type BaseToken = {
  network: number;
  tokenAddress: string;
};

export interface TokenWithNoFilter extends BaseToken {
  filterType: 'no_filter';
}

export interface TokenFilteredByIdRange extends BaseToken {
  minTokenId: string;
  maxTokenId: string;
  filterType: 'id_range';
}

export interface TokenFilteredById extends BaseToken {
  tokenId: string;
  filterType: 'id';
}

export interface TokenFilteredByAttributes extends BaseToken {
  attributeEntries: { traitType: string; value: string | number }[];
  filterType: 'attributes';
  id?: string;
}

export type Token =
  | TokenWithNoFilter
  | TokenFilteredByIdRange
  | TokenFilteredById
  | TokenFilteredByAttributes;

export type TokenFilterType = Token['filterType'];

interface INFT {
  network: number;
  contractAddress: string;
  tokenId: string;
  ownerAddress?: string;
  name?: string;
  image?: string;
  metadata?: object;
  count: string;
  spec: string;
}

export interface Redemption {
  id: number;
  // campaignId: number,
  network: number;
  ownerAddress: string;
  contractAddress: string;
  tokenId: string;
  // index: number,
  nft?: INFT | string;
  submissions?: any;
  redeemedAt?: Date;
  updatedAt?: Date;
}
export interface Question {
  id: number;
  type: number;
  text: string;
  options: string;
  required: boolean;
  index: number;
}

export enum DiscountType {
  Percent,
  Amount,
}

export interface ShopConfiguration {
  primaryDomain: {
    host: string;
    id: string;
  };
  myshopify_domain?: string;
  google_apps_domain?: string;
}

export interface ShopifyProduct {
  shop: string;
  productId: number;
  discountValue: number;
  discountType: DiscountType;
  limit: number;
  total: number | null;
  consumed: number;
  title?: string | null;
  productData: {
    title: string;
    id: number | string;
    handle: string;
    status: string;
    totalInventory: number;
    image: {
      width: number;
      height: number;
      src: string;
    };
    variants: {
      title: string;
      inventory_policy: string;
    }[];
  };
}

export interface ShopifyPriceRule {
  shop: string;
  priceRuleId: number;
  description: string;
}

export interface CampaignShopifyProductRules {
  [productId: number | string]: CampaignShopifyRule[];
}

export enum CampaignShopifyRuleRestrictionType {
  TOKEN,
  TOKEN_ATTRIBUTE,
  TOKEN_RANGE,
  UNRESTRICTED,
  INDIVIDUAL_TOKEN,
  // Server value 5: a hand-picked list of token ids sharing one discount + cap.
  TOKEN_LIST,
}

interface CampaignShopifyRestrictionBase {
  network: number;
  tokenAddress: string;
}

export interface CampaignShopifyTokenAttributeRestriction extends CampaignShopifyRestrictionBase {
  attributeEntries: CampaignShopifyTokenAttributeRestrictionEntry[];
}

export interface CampaignShopifyTokenAttributeRestrictionEntry {
  traitType: string;
  value: string;
}

export type CampaignShopifyTokenRestriction = CampaignShopifyRestrictionBase;

export interface CampaignShopifyIndividualTokenRestriction extends CampaignShopifyRestrictionBase {
  tokenId: string;
}

export interface CampaignShopifyTokenListRestriction extends CampaignShopifyRestrictionBase {
  tokenIds: string[];
}

export interface CampaignShopifyTokenRangeRestriction extends CampaignShopifyRestrictionBase {
  minTokenId: string;
  maxTokenId: string;
}

export type CampaignShopifyRule =
  | CampaignShopifyRule_TOKEN
  | CampaignShopifyRule_INDIVIDUAL_TOKEN
  | CampaignShopifyRule_TOKEN_RANGE
  | CampaignShopifyRule_TOKEN_ATTRIBUTE
  | CampaignShopifyRule_TOKEN_LIST
  | CampaignShopifyRule_UNRESTRICTED;

interface CampaignShopifyRule_TOKEN {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.TOKEN;
  restriction: CampaignShopifyTokenRestriction;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}

interface CampaignShopifyRule_INDIVIDUAL_TOKEN {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.INDIVIDUAL_TOKEN;
  restriction: CampaignShopifyIndividualTokenRestriction;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}

interface CampaignShopifyRule_TOKEN_RANGE {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_RANGE;
  restriction: CampaignShopifyTokenRangeRestriction;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}

interface CampaignShopifyRule_TOKEN_LIST {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_LIST;
  restriction: CampaignShopifyTokenListRestriction;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap, shared by every listed token; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}

interface CampaignShopifyRule_TOKEN_ATTRIBUTE {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_ATTRIBUTE;
  restriction: CampaignShopifyTokenAttributeRestriction;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}

interface CampaignShopifyRule_UNRESTRICTED {
  id: string;
  restrictionType: CampaignShopifyRuleRestrictionType.UNRESTRICTED;
  restriction: undefined;
  discountPercent: number | undefined;
  discountAmount?: number | undefined;
  /** Optional per-discount cap; null = no cap of its own. */
  maxRedemptions?: number | null;
  /** Optional per-discount window (ISO). null = same as the product gate. */
  startDate?: string | null;
  endDate?: string | null;
}
